import { useState, useEffect, useCallback } from 'react';
import { getMembers } from '../api';
import { colours as c, font, gbp, num, shortDate } from '../theme';

const Skeleton = ({ width = '100%', height = '20px', style = {} }) => (
  <div className="skeleton" style={{ width, height, ...style }} />
);

const FILTERS = [
  { key: 'all',    label: 'All'        },
  { key: 'active', label: 'Active'     },
  { key: 'lapsed', label: 'Lapsed'     },
  { key: 'never',  label: 'Never paid' },
];

const statusStyle = (s) =>
  s === 'active' ? S.badgeActive : s === 'lapsed' ? S.badgeLapsed : S.badgeNever;

const statusLabel = (s) =>
  s === 'active' ? 'Active' : s === 'lapsed' ? 'Lapsed' : 'Never paid';

export default function AdminMembers() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);

  // debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMembers(page, 20, debounced, status);
      setRows(res.data.members);
      setCounts(res.data.counts);
      setPagination(res.data.pagination);
    } catch (err) {
      // interceptor handles auth errors
    } finally {
      setLoading(false);
    }
  }, [page, debounced, status]);

  useEffect(() => { load(); }, [load]);

  const pickStatus = (key) => { setStatus(key); setPage(1); setOpenId(null); };

  const summary = counts
    ? `${num(counts.all)} on the books · ${num(counts.active)} active · ${num(counts.lapsed)} lapsed · ${num(counts.never)} never paid`
    : '\u00a0';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* controls */}
      <div style={S.controls}>
        <span style={S.summary}>{summary}</span>

        <div style={S.controlRow} className="stack-sm">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email or phone"
            style={S.search}
          />
          <div style={S.filterGroup}>
            {FILTERS.map((f, i) => (
              <button
                key={f.key}
                onClick={() => pickStatus(f.key)}
                style={{
                  ...(status === f.key ? S.filterOn : S.filterOff),
                  borderLeft: i === 0 ? 'none' : `1px solid ${c.hairline}`,
                }}
              >
                {f.label}{counts ? ` ${num(counts[f.key])}` : ''}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* table */}
      <div style={S.tableCard}>
        <div style={S.headRow} className="hide-mobile">
          <span>Member</span><span>Status</span><span>Plan</span>
          <span>Entries</span><span>Covered until</span><span />
        </div>

        {loading ? (
          <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[...Array(6)].map((_, i) => <Skeleton key={i} height="18px" />)}
          </div>
        ) : rows.length === 0 ? (
          <div style={S.empty}>
            <span style={S.emptyTitle}>No members found</span>
            <span style={S.small}>
              {debounced ? 'Try a different search.' : 'Nobody matches this filter yet.'}
            </span>
          </div>
        ) : (
          rows.map((m) => {
            const open = openId === m.id;
            return (
              <div key={m.id}>
                <div
                  className="row-hover"
                  style={{ ...S.row, background: open ? c.cardAlt : 'transparent' }}
                  onClick={() => setOpenId(open ? null : m.id)}
                >
                  <div style={S.memberCell}>
                    <span style={{ ...S.name, fontWeight: open ? 600 : 400 }}>{m.name}</span>
                    <span style={S.contact}>
                      {m.email}{m.phone ? ` · ${m.phone}` : ''}
                    </span>
                  </div>

                  <span data-label="Status">
                    <span style={statusStyle(m.status)}>{statusLabel(m.status)}</span>
                  </span>

                  <span style={S.plain} data-label="Plan">
                    {m.plan === 'annual' ? 'Annual' : m.plan === 'monthly' ? 'Monthly' : '—'}
                  </span>

                  <span style={S.plain} data-label="Entries">
                    {m.entries == null ? '—' : m.entries}
                  </span>

                  <span
                    style={{
                      ...S.dateCell,
                      color: m.status === 'lapsed' ? c.lapsedFg : c.ink,
                    }}
                    data-label="Covered until"
                  >
                    {m.coverageEnd ? shortDate(m.coverageEnd) : '—'}
                  </span>

                  <span style={S.chev}>{open ? '\u2212' : '\u00b7\u00b7\u00b7'}</span>
                </div>

                {open && (
                  <div style={S.detail} className="stack-sm">
                    <div style={S.detailCell}>
                      <span style={S.detailLabel}>Joined</span>
                      <span style={S.detailValue}>{shortDate(m.created_at)}</span>
                    </div>
                    <div style={S.detailCell}>
                      <span style={S.detailLabel}>Paid to date</span>
                      <span style={S.detailValue}>{gbp(m.paidToDate)}</span>
                    </div>
                    <div style={S.detailCell}>
                      <span style={S.detailLabel}>Payments</span>
                      <span style={S.detailValue}>
                        {m.payments?.length || 0} recorded
                      </span>
                    </div>
                    <div style={S.detailCell}>
                      <span style={S.detailLabel}>Role</span>
                      <span style={S.detailValue}>{m.is_admin ? 'Committee' : 'Member'}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div style={S.pagination}>
            <span style={S.small}>
              Page {pagination.page} of {pagination.totalPages} ({num(pagination.total)} shown)
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={page <= 1 ? S.pageBtnOff : S.pageBtn}
                disabled={page <= 1}
                onClick={() => { setPage(page - 1); setOpenId(null); }}
              >
                Previous
              </button>
              <button
                style={page >= pagination.totalPages ? S.pageBtnOff : S.pageBtn}
                disabled={page >= pagination.totalPages}
                onClick={() => { setPage(page + 1); setOpenId(null); }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const GRID = '2fr 1fr 0.9fr 0.7fr 1.1fr 0.4fr';

const S = {
  controls: {
    display: 'flex', flexDirection: 'column', gap: 18,
    paddingBottom: 18, marginBottom: 0,
  },
  summary: { fontSize: 14, color: c.muted },
  controlRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  search: {
    flex: 1, minWidth: 220, padding: '11px 14px',
    border: `1px solid ${c.hairline}`, background: c.card, borderRadius: 3,
    fontFamily: font.sans, fontSize: 14, color: c.ink,
  },
  filterGroup: {
    display: 'flex', border: `1px solid ${c.hairline}`, borderRadius: 3,
    overflow: 'hidden', background: c.card, flexWrap: 'wrap',
  },
  filterOn: {
    padding: '10px 16px', fontSize: 13, background: c.ink, color: c.onTeal,
    fontWeight: 600, whiteSpace: 'nowrap', border: 'none', cursor: 'pointer',
    fontFamily: font.sans,
  },
  filterOff: {
    padding: '10px 16px', fontSize: 13, background: 'transparent', color: c.body,
    whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', fontFamily: font.sans,
  },

  tableCard: {
    background: c.card, border: `1px solid ${c.hairline}`, borderRadius: 3,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  headRow: {
    display: 'grid', gridTemplateColumns: GRID, padding: '12px 28px',
    borderBottom: `1px solid ${c.hairline}`, fontSize: 11, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: c.muted,
  },
  row: {
    display: 'grid', gridTemplateColumns: GRID, alignItems: 'center',
    padding: '14px 28px', borderBottom: `1px solid ${c.hairSoft}`, cursor: 'pointer',
  },
  memberCell: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  name: { fontSize: 15, color: c.ink },
  contact: {
    fontSize: 12, color: c.muted,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  plain: { fontSize: 14, color: c.body },
  dateCell: { fontSize: 14, fontVariantNumeric: 'tabular-nums' },
  chev: { textAlign: 'right', color: c.muted, fontSize: 16 },

  badgeActive: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 2,
    background: c.activeBg, color: c.activeFg, fontSize: 12, fontWeight: 600,
  },
  badgeLapsed: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 2,
    background: c.lapsedBg, color: c.lapsedFg, fontSize: 12, fontWeight: 600,
  },
  badgeNever: {
    display: 'inline-block', padding: '3px 10px', borderRadius: 2,
    background: c.neverBg, color: c.neverFg, fontSize: 12, fontWeight: 600,
  },

  detail: {
    display: 'flex', gap: 40, alignItems: 'center', flexWrap: 'wrap',
    padding: '4px 28px 20px', background: c.cardAlt,
    borderBottom: `1px solid ${c.hairSoft}`,
  },
  detailCell: { display: 'flex', flexDirection: 'column', gap: 3 },
  detailLabel: {
    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: c.muted,
  },
  detailValue: { fontSize: 14, color: c.ink },

  empty: {
    padding: '40px 28px', display: 'flex', flexDirection: 'column',
    gap: 6, alignItems: 'center', textAlign: 'center',
  },
  emptyTitle: { fontFamily: font.serif, fontSize: 19, color: c.ink },
  small: { fontSize: 13, color: c.muted },

  pagination: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 28px', gap: 12, flexWrap: 'wrap',
  },
  pageBtn: {
    padding: '8px 16px', border: `1px solid ${c.hairline}`, borderRadius: 3,
    background: c.card, cursor: 'pointer', fontSize: 13, color: c.teal,
    fontFamily: font.sans,
  },
  pageBtnOff: {
    padding: '8px 16px', border: `1px solid ${c.hairline}`, borderRadius: 3,
    background: c.cardAlt, cursor: 'not-allowed', fontSize: 13, color: '#a0aec0',
    fontFamily: font.sans,
  },
};
